// Admin console: add or change one E-Learning video group (a course in the API) (/admin/elearning/baru, /admin/elearning/:courseId).
//   header   plain spacing  back to E-Learning, what is being edited
//   course   card           Judul kelompok, Deskripsi, Peran (PA/KPA, PPK, Penyedia; at least one)
//   add      card           Link YouTube and Periksa (POST /learning/admin/youtube): the video lands
//                           at the end of the video list with its title and channel filled in
//   lessons  plain spacing  one card per video: thumbnail, Judul video, Sumber, Durasi, Ringkasan;
//                           Naikkan, Turunkan, Hapus
//   save     plain spacing  the errors, the success notice, Simpan (PUT the whole list) and Batal
// The API saves every group at once, so Simpan sends the full list with this group in place.
// Unsaved changes warn before the tab closes (the same beforeunload guard as the document editor).
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import * as API from "../api";
import { formatCount } from "../lib/format";
import { ROLE_LABEL } from "../lib/labels";
import { navigate } from "../router";
import { Field, fieldAria, Notice, PageHeader, SectionHead, SkeletonRows, useAsync, useTitle } from "../ui/components";
import { Glyph } from "../ui/icons";
import { ADMIN_LEARNING, adminCourseHref } from "./Elearning";

export const LEARNING_ROLES: API.LearningRole[] = ["pa_kpa", "ppk", "penyedia"];

export function thumbnailUrl(youtubeId: string): string {
  return `https://i.ytimg.com/vi/${encodeURIComponent(youtubeId)}/mqdefault.jpg`;
}

interface LessonDraft {
  key: string;
  id?: string;
  youtubeId: string;
  title: string;
  source: string;
  duration: string;
  summary: string;
  /** Set only for a video checked in this session. */
  embeddable?: boolean | null;
}

interface CourseDraft {
  id?: string;
  title: string;
  description: string;
  roles: API.LearningRole[];
  lessons: LessonDraft[];
}

type Errors = Record<string, string>;

let seq = 0;
const nextKey = () => `l${++seq}`;

function toDraft(c: API.AdminCourse | null): CourseDraft {
  if (!c) return { title: "", description: "", roles: [...LEARNING_ROLES], lessons: [] };
  return {
    id: c.id,
    title: c.title,
    description: c.description,
    roles: [...c.roles],
    lessons: c.lessons.map((l) => ({
      key: nextKey(),
      id: l.id,
      youtubeId: l.youtubeId,
      title: l.title,
      source: l.source,
      duration: l.durationMinutes === null ? "" : String(l.durationMinutes),
      summary: l.summary,
    })),
  };
}

function toCourse(d: CourseDraft): API.AdminCourse {
  const course: API.AdminCourse = {
    title: d.title.trim(),
    description: d.description.trim(),
    roles: LEARNING_ROLES.filter((r) => d.roles.includes(r)),
    lessons: d.lessons.map((l) => {
      const lesson: API.AdminLesson = {
        title: l.title.trim(),
        youtubeId: l.youtubeId,
        durationMinutes: l.duration.trim() === "" ? null : Number(l.duration.trim()),
        summary: l.summary.trim(),
        source: l.source.trim(),
      };
      return l.id ? { id: l.id, ...lesson } : lesson;
    }),
  };
  return d.id ? { id: d.id, ...course } : course;
}

/** The checks the API makes, run first so most mistakes are named before Simpan leaves the page. */
export function validateDraft(d: CourseDraft): Errors {
  const e: Errors = {};
  if (!d.title.trim()) e.title = "Isi judul kelompok.";
  else if (d.title.trim().length > 200) e.title = "Judul paling banyak 200 karakter.";
  if (d.description.trim().length > 1000) e.description = "Deskripsi paling banyak 1.000 karakter.";
  if (d.roles.length === 0) e.roles = "Pilih minimal satu peran.";
  if (d.lessons.length === 0) e.lessons = "Tambahkan minimal satu video dari link YouTube.";
  d.lessons.forEach((l, i) => {
    if (!l.title.trim()) e[`lessons.${i}.title`] = "Isi judul video.";
    else if (l.title.trim().length > 200) e[`lessons.${i}.title`] = "Judul paling banyak 200 karakter.";
    if (l.source.trim().length > 200) e[`lessons.${i}.source`] = "Sumber paling banyak 200 karakter.";
    if (l.summary.trim().length > 500) e[`lessons.${i}.summary`] = "Ringkasan paling banyak 500 karakter.";
    const dur = l.duration.trim();
    if (dur && !(/^\d+$/.test(dur) && Number(dur) > 0)) e[`lessons.${i}.duration`] = "Isi menit sebagai angka bulat, atau kosongkan.";
  });
  return e;
}

/**
 * Field errors from a 422 message such as
 * `kolom "courses.2.lessons.0.title" wajib diisi; kolom "courses.2.roles" ...`,
 * for the course at `index`. durationMinutes maps to the duration field.
 */
export function errorsFrom422(message: string, index: number): Errors {
  const out: Errors = {};
  const re = /kolom\s+"?courses\.(\d+)\.([\w.]+)"?\s+([^;)]+)/g;
  for (const m of message.matchAll(re)) {
    if (Number(m[1]) !== index) continue;
    const path = m[2]!.replace(/durationMinutes$/, "duration").replace(/^lessons\.(\d+)\.youtubeId$/, "lessons.$1.title");
    out[path] ??= m[3]!.trim();
  }
  return out;
}

export function ElearningEditorScreen({ courseId }: { courseId?: string }) {
  const list = useAsync(() => API.listAdminCourses(), []);
  useTitle(courseId ? "Ubah kelompok" : "Tambah kelompok");
  const back = { href: ADMIN_LEARNING, label: "E-Learning" };

  if (list.loading && !list.data) {
    return (
      <>
        <PageHeader title={courseId ? "Memuat kelompok video" : "Tambah kelompok"} back={back} titleSize="document" />
        <SkeletonRows count={4} label="Memuat kelompok video" />
      </>
    );
  }
  if (list.error || !list.data) {
    return (
      <>
        <PageHeader title="Kelompok video belum dapat dimuat" back={back} titleSize="document" />
        <Notice
          tone="danger"
          title="Terjadi kesalahan"
          action={
            <button type="button" className="btn btn-secondary" onClick={list.reload}>
              Muat ulang
            </button>
          }
        >
          <p>{API.errorMessage(list.error)}</p>
        </Notice>
      </>
    );
  }
  const index = courseId ? list.data.findIndex((c) => c.id === courseId) : -1;
  if (courseId && index === -1) {
    return (
      <>
        <PageHeader title="Kelompok video tidak ditemukan" back={back} titleSize="document" />
        <Notice
          tone="info"
          title="Kelompok video ini tidak ada atau sudah dihapus"
          action={
            <a className="btn btn-secondary" href={ADMIN_LEARNING}>
              Kembali ke E-Learning
            </a>
          }
        />
      </>
    );
  }
  return <CourseForm all={list.data} initialIndex={index === -1 ? null : index} onSaved={list.set} />;
}

function CourseForm({ all, initialIndex, onSaved }: { all: API.AdminCourse[]; initialIndex: number | null; onSaved: (c: API.AdminCourse[]) => void }) {
  const ids = useId();
  const [index, setIndex] = useState<number | null>(initialIndex);
  const [draft, setDraft] = useState<CourseDraft>(() => toDraft(initialIndex === null ? null : all[initialIndex]!));
  const [baseline, setBaseline] = useState(() => JSON.stringify(toCourse(draft)));
  const [errors, setErrors] = useState<Errors>({});
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<{ tone: "success" | "danger"; title: string; text: string } | null>(null);
  const dirty = JSON.stringify(toCourse(draft)) !== baseline;

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const set = (patch: Partial<CourseDraft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setResult(null);
  };
  const setLesson = (key: string, patch: Partial<LessonDraft>) => {
    setDraft((d) => ({ ...d, lessons: d.lessons.map((l) => (l.key === key ? { ...l, ...patch } : l)) }));
    setResult(null);
  };
  const moveLesson = (i: number, by: -1 | 1) => {
    setDraft((d) => {
      const j = i + by;
      if (j < 0 || j >= d.lessons.length) return d;
      const lessons = [...d.lessons];
      [lessons[i], lessons[j]] = [lessons[j]!, lessons[i]!];
      return { ...d, lessons };
    });
    setErrors({});
    setResult(null);
  };
  const removeLesson = (key: string) => {
    setDraft((d) => ({ ...d, lessons: d.lessons.filter((l) => l.key !== key) }));
    setErrors({});
    setResult(null);
  };

  async function save(e: FormEvent) {
    e.preventDefault();
    const found = validateDraft(draft);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setResult({ tone: "danger", title: "Periksa kembali isian", text: "Beberapa isian belum lengkap atau belum sesuai. Lihat pesan di bawah setiap isian." });
      return;
    }
    const course = toCourse(draft);
    const at = index ?? all.length;
    const next = index === null ? [...all, course] : all.map((c, i) => (i === index ? course : c));
    setSaving(true);
    setResult(null);
    try {
      const saved = await API.saveAdminCourses(next);
      const mine = saved[at] ?? null;
      const fresh = toDraft(mine);
      onSaved(saved);
      setIndex(at);
      setDraft(fresh);
      setBaseline(JSON.stringify(toCourse(fresh)));
      setErrors({});
      setResult({ tone: "success", title: "Kelompok video disimpan", text: "Pengguna dengan peran terpilih sudah dapat menonton video di kelompok ini." });
      if (mine?.id && index === null) navigate(adminCourseHref(mine.id), { replace: true });
    } catch (err) {
      const message = API.errorMessage(err);
      const fields = err instanceof API.ApiError && err.status === 422 ? errorsFrom422(message, at) : {};
      setErrors(fields);
      setResult({ tone: "danger", title: "Kelompok video belum disimpan", text: message });
    } finally {
      setSaving(false);
    }
  }

  const titleId = `${ids}title`;
  const descId = `${ids}desc`;

  return (
    <>
      <PageHeader
        title={index === null ? "Tambah kelompok" : draft.title.trim() || "Ubah kelompok"}
        back={{ href: ADMIN_LEARNING, label: "E-Learning" }}
        titleSize="document"
        lead={index === null ? "Isi data kelompok, tambahkan video dari link YouTube, lalu simpan." : "Perubahan berlaku untuk pengguna setelah disimpan."}
      />

      <form className="el-form" onSubmit={save} noValidate aria-label="Data kelompok video">
        <section className="card form-card" aria-labelledby={`${ids}course`}>
          <h2 id={`${ids}course`} className="card-title">
            Kelompok video
          </h2>
          <Field id={titleId} label="Judul kelompok" required error={errors.title}>
            <input {...fieldAria(titleId, errors.title)} value={draft.title} maxLength={200} onChange={(e) => set({ title: e.currentTarget.value })} />
          </Field>
          <Field id={descId} label="Deskripsi" hint="Satu atau dua kalimat tentang isi kelompok ini." error={errors.description}>
            <textarea {...fieldAria(descId, errors.description)} rows={3} value={draft.description} maxLength={1000} onChange={(e) => set({ description: e.currentTarget.value })} />
          </Field>
          <fieldset className="el-roles" aria-describedby={`${ids}roles-hint`}>
            <legend className="field-label">
              Peran yang dapat menonton
              <span className="req" aria-hidden="true">
                {" "}
                *
              </span>
            </legend>
            <div className="el-role-options">
              {LEARNING_ROLES.map((r) => (
                <label key={r} className="ui-checkbox">
                  <input
                    type="checkbox"
                    checked={draft.roles.includes(r)}
                    aria-invalid={errors.roles ? true : undefined}
                    onChange={(e) => {
                      const on = e.currentTarget.checked;
                      set({ roles: on ? [...draft.roles, r] : draft.roles.filter((x) => x !== r) });
                    }}
                  />
                  <span>{ROLE_LABEL[r]}</span>
                </label>
              ))}
            </div>
            <p className="field-hint" id={`${ids}roles-hint`} data-state={errors.roles ? "error" : undefined} aria-live={errors.roles ? "polite" : undefined}>
              {errors.roles ? (
                <>
                  <Glyph name="danger" size={16} />
                  <span>{errors.roles}</span>
                </>
              ) : (
                "Administrator tidak memiliki tampilan E-Learning."
              )}
            </p>
          </fieldset>
        </section>

        <section className="region" aria-labelledby={`${ids}lessons`}>
          <SectionHead
            id={`${ids}lessons`}
            title={`Video (${formatCount(draft.lessons.length)})`}
            lead="Urutan di sini adalah urutan tampil video di kelompok ini. Durasi boleh dikosongkan."
          />
          <AddLesson
            onAdd={(info) => {
              const key = nextKey();
              setDraft((d) => ({
                ...d,
                lessons: [...d.lessons, { key, youtubeId: info.youtubeId, title: info.title ?? "", source: info.channel ?? "", duration: "", summary: "", embeddable: info.embeddable }],
              }));
              setErrors((e) => {
                const { lessons: _l, ...rest } = e;
                return rest;
              });
              setResult(null);
            }}
          />
          {errors.lessons ? (
            <p className="field-hint" data-state="error" role="alert">
              <Glyph name="danger" size={16} />
              <span>{errors.lessons}</span>
            </p>
          ) : null}
          {draft.lessons.length > 0 ? (
            <ol className="el-lessons">
              {draft.lessons.map((l, i) => (
                <LessonEditor
                  key={l.key}
                  lesson={l}
                  index={i}
                  count={draft.lessons.length}
                  errors={errors}
                  onChange={(patch) => setLesson(l.key, patch)}
                  onMove={(by) => moveLesson(i, by)}
                  onRemove={() => removeLesson(l.key)}
                />
              ))}
            </ol>
          ) : null}
        </section>

        <div className="el-save">
          {result ? (
            <Notice tone={result.tone} title={result.title}>
              <p>{result.text}</p>
            </Notice>
          ) : null}
          <div className="button-row">
            <button type="submit" className="btn" aria-busy={saving || undefined} disabled={saving}>
              <Glyph name="tickCircle" />
              <span>{saving ? "Menyimpan" : "Simpan"}</span>
            </button>
            <a className="btn btn-secondary" href={ADMIN_LEARNING}>
              {dirty ? "Batal" : "Kembali"}
            </a>
            {dirty ? <span className="form-note">Ada perubahan yang belum disimpan.</span> : null}
          </div>
        </div>
      </form>
    </>
  );
}

function AddLesson({ onAdd }: { onAdd: (info: API.YoutubeInfo) => void }) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState("");
  const [state, setState] = useState<{ busy: boolean; error: string | null; added: string | null }>({ busy: false, error: null, added: null });

  async function check() {
    if (!url.trim()) {
      setState({ busy: false, error: "Tempel link video YouTube terlebih dahulu.", added: null });
      input.current?.focus();
      return;
    }
    setState({ busy: true, error: null, added: null });
    try {
      const info = await API.checkYoutube(url.trim());
      onAdd(info);
      setUrl("");
      setState({ busy: false, error: null, added: info.title ?? info.youtubeId });
    } catch (err) {
      setState({ busy: false, error: API.errorMessage(err), added: null });
    }
  }

  return (
    <div className="card el-add">
      <div className="el-add-row">
        <Field id={`${id}url`} label="Link YouTube" error={state.error} hint={state.added ? `Ditambahkan: ${state.added}. Periksa isiannya di bawah.` : "Contoh: https://www.youtube.com/watch?v=..."}>
          <input
            ref={input}
            {...fieldAria(`${id}url`, state.error)}
            type="url"
            inputMode="url"
            autoComplete="off"
            placeholder="https://www.youtube.com/watch?v=..."
            value={url}
            onChange={(e) => setUrl(e.currentTarget.value)}
            onKeyDown={(e) => {
              // Enter checks the link instead of submitting the whole group.
              if (e.key === "Enter") {
                e.preventDefault();
                void check();
              }
            }}
          />
        </Field>
        <button type="button" className="btn btn-secondary el-add-btn" onClick={check} disabled={state.busy} aria-busy={state.busy || undefined}>
          <Glyph name="searchNormal" />
          <span>{state.busy ? "Memeriksa" : "Periksa"}</span>
        </button>
      </div>
    </div>
  );
}

function LessonEditor({
  lesson,
  index,
  count,
  errors,
  onChange,
  onMove,
  onRemove,
}: {
  lesson: LessonDraft;
  index: number;
  count: number;
  errors: Errors;
  onChange: (patch: Partial<LessonDraft>) => void;
  onMove: (by: -1 | 1) => void;
  onRemove: () => void;
}) {
  const id = useId();
  const err = (f: string) => errors[`lessons.${index}.${f}`];
  const name = lesson.title.trim() || `video ${index + 1}`;
  return (
    <li className="card el-lesson" aria-labelledby={`${id}head`}>
      <div className="el-lesson-head">
        <img className="el-thumb" src={thumbnailUrl(lesson.youtubeId)} alt="" width={160} height={90} loading="lazy" />
        <div className="el-lesson-id">
          <h3 id={`${id}head`} className="card-subtitle">
            Video <span className="num">{index + 1}</span>
          </h3>
          <p className="row-meta">
            Video <span className="mono">{lesson.youtubeId}</span>
          </p>
        </div>
        <div className="row-actions el-lesson-actions">
          <button type="button" className="ui-icon-btn" aria-label={`Naikkan ${name}`} title="Naikkan" disabled={index === 0} onClick={() => onMove(-1)}>
            <span className="glyph-up">
              <Glyph name="chevronRight" />
            </span>
          </button>
          <button type="button" className="ui-icon-btn" aria-label={`Turunkan ${name}`} title="Turunkan" disabled={index === count - 1} onClick={() => onMove(1)}>
            <span className="glyph-down">
              <Glyph name="chevronRight" />
            </span>
          </button>
          <button type="button" className="ui-icon-btn" aria-label={`Hapus ${name}`} title="Hapus" onClick={onRemove}>
            <Glyph name="trash" />
          </button>
        </div>
      </div>

      {lesson.embeddable === false ? (
        <Notice tone="warning" title="Video ini tidak dapat diputar di platform">
          <p>Pemilik video menonaktifkan penyematan. Pilih video lain atau minta pemilik mengizinkannya.</p>
        </Notice>
      ) : lesson.embeddable === null ? (
        <Notice tone="warning" title="Video belum dapat diperiksa">
          <p>Judul dan sumber tidak terisi otomatis. Isi sendiri, lalu pastikan video dapat diputar setelah disimpan.</p>
        </Notice>
      ) : null}

      <div className="field-row">
        <Field id={`${id}title`} label="Judul video" required error={err("title")}>
          <input {...fieldAria(`${id}title`, err("title"))} value={lesson.title} maxLength={200} onChange={(e) => onChange({ title: e.currentTarget.value })} />
        </Field>
        <Field id={`${id}source`} label="Sumber" hint="Nama kanal YouTube." error={err("source")}>
          <input {...fieldAria(`${id}source`, err("source"))} value={lesson.source} maxLength={200} onChange={(e) => onChange({ source: e.currentTarget.value })} />
        </Field>
      </div>
      <div className="field-row">
        <Field id={`${id}duration`} label="Durasi (menit)" hint="Boleh dikosongkan." error={err("duration")}>
          <input
            {...fieldAria(`${id}duration`, err("duration"))}
            inputMode="numeric"
            value={lesson.duration}
            onChange={(e) => onChange({ duration: e.currentTarget.value.replace(/[^\d]/g, "") })}
          />
        </Field>
      </div>
      <Field id={`${id}summary`} label="Ringkasan" hint="Paling banyak 500 karakter." error={err("summary")}>
        <textarea {...fieldAria(`${id}summary`, err("summary"))} rows={3} maxLength={500} value={lesson.summary} onChange={(e) => onChange({ summary: e.currentTarget.value })} />
      </Field>
    </li>
  );
}
